'use client';

import { useState, useEffect, useCallback } from 'react';

const CEO_PIN = '5050';
const REFRESH_INTERVAL = 30000;

interface Metrics {
  timestamp: string;
  totals: {
    workers: number;
    contractors: number;
    jobs: number;
    activeJobs: number;
    matches: number;
    hires: number;
    payments: number;
    ratings: number;
    feedback: number;
    newFeedback: number;
    pendingMatches: number;
  };
  revenue: {
    total: number;
    perHire: number;
    currency: string;
    successRate: number;
    totalAttempted: number;
    dailyTrend: Array<{ date: string; amount: number; hires: number }>;
  };
  activity: {
    signups24h: { workers: number; contractors: number; total: number };
    signups7d: { workers: number; contractors: number; total: number };
    dailyTrend: Array<{ date: string; workers: number; contractors: number }>;
  };
  funnel: { hireRate: number; pendingMatches: number };
  recent: {
    hires: Array<{ id: string; hiredAt: string }>;
    jobs: Array<{ id: string; title: string; status: string; createdAt: string }>;
  };
  geography: {
    topLocations: Array<{ area: string; total: number; workers: number; contractors: number }>;
    stateDistribution: Array<{ state: string; count: number }>;
  };
}

function timeAgo(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function MetricsPage() {
  const [pin, setPin] = useState('');
  const [authed, setAuthed] = useState(false);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ceo/metrics', {
        headers: { 'x-ceo-pin': CEO_PIN },
      });
      if (res.ok) {
        const data = await res.json();
        setMetrics(data);
        setLastRefresh(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) {
      fetchMetrics();
      const interval = setInterval(fetchMetrics, REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [authed, fetchMetrics]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-xl p-6 w-full max-w-xs">
          <h1 className="text-white text-lg font-bold mb-4 text-center">📊 Launch Metrics</h1>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && pin === CEO_PIN) setAuthed(true); }}
            placeholder="Enter PIN"
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 text-center text-xl tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={4}
            autoFocus
          />
          <button
            onClick={() => { if (pin === CEO_PIN) setAuthed(true); }}
            className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 font-medium transition-colors"
          >
            Enter
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Loading metrics...</p>
      </div>
    );
  }

  const t = metrics.totals;
  const a = metrics.activity;

  const exportCSV = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Workers', t.workers],
      ['Contractors', t.contractors],
      ['Total Jobs', t.jobs],
      ['Active Jobs', t.activeJobs],
      ['Total Matches', t.matches],
      ['Hires', t.hires],
      ['Revenue (AUD)', metrics.revenue.total],
      ['Hire Rate %', metrics.funnel.hireRate],
      ['Payment Success Rate %', metrics.revenue.successRate],
      ['Signups 24h', a.signups24h.total],
      ['Signups 7d', a.signups7d.total],
      ['Pending Matches', t.pendingMatches],
      ['Ratings', t.ratings],
      ['Feedback', t.feedback],
      ['', ''],
      ['Date', 'Workers Signups', 'Contractor Signups'],
      ...a.dailyTrend.map(d => [d.date, d.workers, d.contractors]),
      ['', ''],
      ['Date', 'Revenue ($)', 'Hires'],
      ...metrics.revenue.dailyTrend.map(d => [d.date, d.amount, d.hires]),
      ['', ''],
      ['State', 'Companies'],
      ...metrics.geography.stateDistribution.map(s => [s.state, s.count]),
      ['', ''],
      ['Area', 'Workers', 'Contractors', 'Total'],
      ...metrics.geography.topLocations.map(l => [l.area, l.workers, l.contractors, l.total]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a2 = document.createElement('a');
    a2.href = url;
    a2.download = `rateright-metrics-${new Date().toISOString().slice(0, 10)}.csv`;
    a2.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-20">
      {/* Header */}
      <div className="border-b border-gray-800 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">📊 Launch Metrics</h1>
            <p className="text-gray-400 text-xs mt-1">
              {lastRefresh ? `Updated ${timeAgo(lastRefresh.toISOString())}` : ''} · Auto-refreshes every 30s
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCSV} className="text-sm bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg">
              📥 CSV
            </button>
            <button onClick={fetchMetrics} className="text-sm bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg">
              {loading ? '↻' : '↻ Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Revenue Banner */}
        <div className="bg-gradient-to-r from-green-900/50 to-green-800/30 rounded-2xl p-6 border border-green-700/30">
          <p className="text-green-400 text-sm font-medium">Revenue</p>
          <p className="text-4xl font-bold text-green-300 mt-1">
            ${metrics.revenue.total.toLocaleString()} <span className="text-lg text-green-400/60">AUD</span>
          </p>
          <div className="flex items-center gap-4 mt-1">
            <p className="text-green-400/60 text-sm">{t.payments} hires × $50</p>
            <p className="text-green-400/60 text-sm">💳 {metrics.revenue.successRate}% payment success</p>
          </div>
        </div>

        {/* Daily Revenue Trend */}
        {metrics.revenue.dailyTrend.some(d => d.amount > 0) && (
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <h3 className="text-sm font-medium text-gray-400 mb-3">💰 Daily Revenue (7d)</h3>
            <div className="space-y-1">
              {metrics.revenue.dailyTrend.map((day) => {
                const maxAmount = Math.max(...metrics.revenue.dailyTrend.map(d => d.amount), 1);
                return (
                  <div key={day.date} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20 shrink-0">{day.date.slice(5)}</span>
                    <div className="flex-1 h-5 bg-gray-800 rounded-full overflow-hidden">
                      {day.amount > 0 && (
                        <div
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${(day.amount / maxAmount) * 100}%` }}
                        />
                      )}
                    </div>
                    <span className="text-xs text-gray-400 w-16 text-right">
                      ${day.amount} <span className="text-gray-600">({day.hires})</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Workers" value={t.workers} emoji="👷" />
          <MetricCard label="Contractors" value={t.contractors} emoji="🏗️" />
          <MetricCard label="Active Jobs" value={t.activeJobs} subtitle={`${t.jobs} total`} emoji="📋" />
          <MetricCard label="Hires" value={t.hires} subtitle={`${metrics.funnel.hireRate}% rate`} emoji="✅" />
        </div>

        {/* Activity Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Last 24h */}
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <h3 className="text-sm font-medium text-gray-400 mb-3">📈 Last 24 Hours</h3>
            <div className="space-y-2">
              <ActivityRow label="New signups" value={a.signups24h.total} />
              <ActivityRow label="  Workers" value={a.signups24h.workers} indent />
              <ActivityRow label="  Contractors" value={a.signups24h.contractors} indent />
            </div>
          </div>

          {/* Last 7 days */}
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <h3 className="text-sm font-medium text-gray-400 mb-3">📊 Last 7 Days</h3>
            <div className="space-y-2">
              <ActivityRow label="New signups" value={a.signups7d.total} />
              <ActivityRow label="  Workers" value={a.signups7d.workers} indent />
              <ActivityRow label="  Contractors" value={a.signups7d.contractors} indent />
            </div>
          </div>
        </div>

        {/* Daily Trend */}
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <h3 className="text-sm font-medium text-gray-400 mb-3">📅 Daily Signups (7-day trend)</h3>
          <div className="space-y-1">
            {a.dailyTrend.map((day) => {
              const total = day.workers + day.contractors;
              const maxBar = Math.max(...a.dailyTrend.map(d => d.workers + d.contractors), 1);
              return (
                <div key={day.date} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-20 shrink-0">{day.date.slice(5)}</span>
                  <div className="flex-1 h-5 bg-gray-800 rounded-full overflow-hidden flex">
                    {day.workers > 0 && (
                      <div
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${(day.workers / maxBar) * 100}%` }}
                      />
                    )}
                    {day.contractors > 0 && (
                      <div
                        className="h-full bg-orange-500 transition-all"
                        style={{ width: `${(day.contractors / maxBar) * 100}%` }}
                      />
                    )}
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right">{total}</span>
                </div>
              );
            })}
            <div className="flex gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Workers</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Contractors</span>
            </div>
          </div>
        </div>

        {/* Pipeline & Feedback */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Pending Matches" value={t.pendingMatches} emoji="⏳" />
          <MetricCard label="Ratings" value={t.ratings} emoji="⭐" />
          <MetricCard label="Feedback" value={t.feedback} subtitle={t.newFeedback > 0 ? `${t.newFeedback} new` : undefined} emoji="💬" />
          <MetricCard label="Total Jobs" value={t.jobs} emoji="📝" />
        </div>

        {/* Recent Jobs */}
        {metrics.recent.jobs.length > 0 && (
          <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
            <h3 className="text-sm font-medium text-gray-400 mb-3">🆕 Recent Jobs (7d)</h3>
            <div className="space-y-2">
              {metrics.recent.jobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between text-sm">
                  <span className="text-white truncate flex-1">{job.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ml-2 ${
                    job.status === 'active' ? 'bg-green-900/50 text-green-400'
                    : job.status === 'filled' ? 'bg-blue-900/50 text-blue-400'
                    : 'bg-gray-800 text-gray-400'
                  }`}>{job.status}</span>
                  <span className="text-xs text-gray-500 ml-2 shrink-0">{timeAgo(job.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Geographic Spread */}
        {(metrics.geography.topLocations.length > 0 || metrics.geography.stateDistribution.length > 0) && (
          <div className="space-y-3">
            {/* State Distribution */}
            {metrics.geography.stateDistribution.length > 0 && (
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <h3 className="text-sm font-medium text-gray-400 mb-3">🗺️ By State</h3>
                <div className="flex flex-wrap gap-2">
                  {metrics.geography.stateDistribution.map(({ state, count }) => {
                    const maxCount = metrics.geography.stateDistribution[0]?.count || 1;
                    const intensity = Math.max(0.3, count / maxCount);
                    return (
                      <div key={state} className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2 text-center" style={{ opacity: intensity + 0.3 }}>
                        <div className="text-lg font-bold text-blue-300">{count}</div>
                        <div className="text-xs text-gray-400">{state}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top Locations */}
            {metrics.geography.topLocations.length > 0 && (
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <h3 className="text-sm font-medium text-gray-400 mb-3">📍 Top Areas</h3>
                <div className="space-y-1">
                  {metrics.geography.topLocations.map((loc) => {
                    const maxTotal = metrics.geography.topLocations[0]?.total || 1;
                    return (
                      <div key={loc.area} className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-28 shrink-0 truncate">{loc.area}</span>
                        <div className="flex-1 h-4 bg-gray-800 rounded-full overflow-hidden flex">
                          {loc.workers > 0 && (
                            <div className="h-full bg-blue-500" style={{ width: `${(loc.workers / maxTotal) * 100}%` }} />
                          )}
                          {loc.contractors > 0 && (
                            <div className="h-full bg-orange-500" style={{ width: `${(loc.contractors / maxTotal) * 100}%` }} />
                          )}
                        </div>
                        <span className="text-xs text-gray-400 w-6 text-right">{loc.total}</span>
                      </div>
                    );
                  })}
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Workers</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Contractors</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, subtitle, emoji }: { label: string; value: number; subtitle?: string; emoji: string }) {
  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{label}</span>
        <span>{emoji}</span>
      </div>
      <p className="text-2xl font-bold mt-1">{value.toLocaleString()}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function ActivityRow({ label, value, indent }: { label: string; value: number; indent?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${indent ? 'ml-2' : ''}`}>
      <span className={`text-sm ${indent ? 'text-gray-500' : 'text-gray-300'}`}>{label}</span>
      <span className={`text-sm font-medium ${value > 0 ? 'text-white' : 'text-gray-600'}`}>{value}</span>
    </div>
  );
}
